/**
 * Phase 13.5 — Hearth email catalog (static manifest).
 *
 * Single source of truth for what emails Hearth sends, who triggers
 * them, when, and what variables go into each. The `/settings/emails`
 * admin page reads this manifest to render a per-template opt-in
 * toggle, optional household-recipient routing, and a fixture-driven
 * preview iframe.
 *
 * Why static (vs runtime EventBridge introspection): a runtime read
 * would require `events:DescribeRule` IAM grants + ListSchedules cost
 * + a new API route. Manual sync drift is bounded — when Andre adds a
 * template, he updates this file. Phase 17+ ops-hardening can
 * auto-regenerate from the CDK stack.
 *
 * **Opt-out key**: most entries use the SES template name (1:1 trigger);
 * a few (anniversary, money-date, signal-fired) are LOGICAL purpose
 * names because the trigger fans out via the shared `messages-unread`
 * template — opting out of "messages-unread" wholesale would silence
 * actual spousal messages. Trigger Lambdas in 13.5 scope check
 * `email_opt_outs[opt_out_key]` against the per-purpose key below.
 *
 * **Routable**: only household-wide digests/audits route to a different
 * member. Per-user templates (welcome, invitation, anniversary-self)
 * always go to the user.
 *
 * Categories: `transactional` (account-required UX, NOT opt-outable),
 * `digest` (periodic rollups), `behavioral` (coaching nudges),
 * `ops` (system / integration alerts).
 */

export type EmailCategory =
  | "transactional"
  | "digest"
  | "behavioral"
  | "ops";

export interface EmailCatalogEntry {
  readonly opt_out_key: string;
  readonly template_name: string;
  readonly title: string;
  readonly description: string;
  readonly category: EmailCategory;
  readonly trigger_lambda: string;
  readonly schedule_human: string;
  readonly schedule_cron?: string;
  readonly recipients: "user-self" | "household-wide";
  readonly opt_outable: boolean;
  readonly routable: boolean;
  readonly variables: ReadonlyArray<string>;
  readonly fixture_path: string;
}

export const EMAIL_CATALOG: readonly EmailCatalogEntry[] = [
  // === Transactional (not opt-outable — account UX requires them) =========
  {
    opt_out_key: "welcome",
    template_name: "welcome",
    title: "Welcome to Hearth",
    description:
      "Sent once when you first activate your Hearth account. Confirms your sign-in works and links to the dashboard.",
    category: "transactional",
    trigger_lambda: "(Cognito post-confirmation handler)",
    schedule_human: "Once on first sign-up",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: ["display_name", "site_url", "tenant_name"],
    fixture_path: "infra/email/fixtures/welcome.json",
  },
  {
    opt_out_key: "invitation",
    template_name: "invitation",
    title: "Household invitation",
    description:
      "Sent when an owner/admin invites a new member into the household.",
    category: "transactional",
    trigger_lambda: "hearth-{env}-tenants (HouseholdsFn)",
    schedule_human: "On invitation create",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: [
      "expires_at_human",
      "household_name",
      "inviter_display_name",
      "role",
      "signup_url",
    ],
    fixture_path: "infra/email/fixtures/invitation.json",
  },
  {
    opt_out_key: "messages-unread",
    template_name: "messages-unread",
    title: "Spousal message",
    description:
      "Sent when a household member messages you. Threaded conversations pinned to entities.",
    category: "transactional",
    trigger_lambda: "hearth-{env}-messages-notify (MessagesNotifyFn — DDB stream)",
    schedule_human: "On unread message",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: [
      "body_preview",
      "messages_url",
      "sender_display_name",
      "sent_at_human",
      "thread_title",
    ],
    fixture_path: "infra/email/fixtures/messages-unread.json",
  },

  // === Digests (regular rollups; opt-outable + routable when household-wide) ==
  {
    opt_out_key: "bills-digest",
    template_name: "bills-digest",
    title: "Weekly bills digest",
    description:
      "Bills coming due in the next 7 days plus anything overdue. Helpful for spotting auto-pay drift.",
    category: "digest",
    trigger_lambda: "hearth-{env}-bills-digest (BillsDigestFn)",
    schedule_human: "Sundays at 9am ET",
    schedule_cron: "cron(0 13 ? * SUN *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "household_name",
      "week_start_human",
      "due_this_week_count",
      "overdue_count",
      "total_due_human",
      "due_this_week_bills",
      "overdue_bills",
      "has_overdue",
      "has_due_this_week",
      "bills_url",
    ],
    fixture_path: "infra/email/fixtures/bills-digest.json",
  },
  {
    opt_out_key: "monthly-summary-ready",
    template_name: "monthly-summary-ready",
    title: "Monthly summary",
    description:
      "Net worth, spending, saving rate and notable events from the prior month, packaged as a PDF.",
    category: "digest",
    trigger_lambda: "hearth-{env}-monthly-summary (MonthlySummaryFn)",
    schedule_human: "1st of each month at 8am ET",
    schedule_cron: "cron(0 12 1 * ? *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: ["display_name", "download_url", "household_name", "period"],
    fixture_path: "infra/email/fixtures/monthly-summary-ready.json",
  },
  {
    opt_out_key: "annual-review-ready",
    template_name: "annual-review-ready",
    title: "Annual review packet",
    description:
      "End-of-year financial review: net-worth delta, contributions, donations, FIRE progress, tax-handoff packet.",
    category: "digest",
    trigger_lambda: "hearth-{env}-annual-review (AnnualReviewFn)",
    schedule_human: "January 5th at 8am ET",
    schedule_cron: "cron(0 13 5 1 ? *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "display_name",
      "donations_count",
      "donations_total_dollars",
      "household_name",
      "pdf_url",
      "site_domain",
      "tax_year",
    ],
    fixture_path: "infra/email/fixtures/annual-review-ready.json",
  },
  {
    opt_out_key: "money-date",
    template_name: "messages-unread",
    title: "Monthly money date prompt",
    description:
      "First Saturday of each month — a thread-starter for you and your spouse to talk about the household money picture.",
    category: "digest",
    trigger_lambda: "hearth-{env}-money-date (MoneyDateFn)",
    schedule_human: "1st Saturday of each month at 7pm ET",
    schedule_cron: "cron(0 23 ? * SAT#1 *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "body_preview",
      "messages_url",
      "sender_display_name",
      "sent_at_human",
      "thread_title",
    ],
    fixture_path: "infra/email/fixtures/messages-unread.json",
  },
  {
    opt_out_key: "quarterly-tax-projection",
    template_name: "quarterly-tax-projection",
    title: "Quarterly tax projection",
    description:
      "Estimated federal + NY + NYC tax liability for the upcoming quarter, plus safe-harbor status.",
    category: "digest",
    trigger_lambda: "hearth-{env}-tax (TaxFn)",
    schedule_human: "Quarterly (Mar 1 / May 15 / Aug 15 / Nov 15) at 8am ET",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "household_name",
      "quarter_human",
      "tax_year",
      "projected_total_human",
      "projected_federal_human",
      "projected_ny_human",
      "projected_nyc_human",
      "projected_annual_ordinary_human",
      "projected_annual_qdivs_human",
      "projected_annual_ltcg_human",
      "composite_effective_rate_pct",
      "composite_marginal_rate_pct",
      "safe_harbor_safe",
      "safe_harbor_status_human",
      "safe_harbor_shortfall_human",
      "tax_url",
    ],
    fixture_path: "infra/email/fixtures/quarterly-tax-projection.json",
  },

  // === Behavioral (Phase 13 coaching surfaces) ============================
  {
    opt_out_key: "lifestyle-inflation-flagged",
    template_name: "lifestyle-inflation-flagged",
    title: "Lifestyle inflation flagged",
    description:
      "Categories where YoY spending growth is outpacing your income growth by more than your warn threshold.",
    category: "behavioral",
    trigger_lambda: "hearth-{env}-lifestyle-inflation-audit (LifestyleInflationAuditFn)",
    schedule_human: "15th of each month at 9am ET",
    schedule_cron: "cron(0 13 15 * ? *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "category_count",
      "creep_dollars",
      "display_name",
      "household_name",
      "income_growth_pct",
      "spending_url",
      "summary",
      "threshold_pct",
      "warn_pp",
    ],
    fixture_path: "infra/email/fixtures/lifestyle-inflation-flagged.json",
  },
  {
    opt_out_key: "welcome-back-summary",
    template_name: "welcome-back-summary",
    title: "Welcome-back summary",
    description:
      "Sent if you haven't logged in for 14+ days. Summarizes recent activity and bills coming due.",
    category: "behavioral",
    trigger_lambda: "hearth-{env}-login-gap-audit (LoginGapAuditFn)",
    schedule_human: "Nightly at 7pm ET (sent only if last login > 14 days ago)",
    schedule_cron: "cron(0 23 * * ? *)",
    recipients: "user-self",
    opt_outable: true,
    routable: false,
    variables: [
      "activity_count",
      "bills_count",
      "bills_summary",
      "display_name",
      "household_name",
      "last_login_label",
      "sign_in_url",
    ],
    fixture_path: "infra/email/fixtures/welcome-back-summary.json",
  },
  {
    opt_out_key: "anniversary-celebration",
    template_name: "messages-unread",
    title: "Anniversary celebration",
    description:
      "Birthday / wedding anniversary / work anniversary nudges via the household activity feed + an email.",
    category: "behavioral",
    trigger_lambda: "hearth-{env}-anniversary-celebration (AnniversaryCelebrationFn)",
    schedule_human: "Daily at 7am ET (only fires on matching MM-DD)",
    schedule_cron: "cron(0 11 * * ? *)",
    recipients: "user-self",
    opt_outable: true,
    routable: false,
    variables: [
      "body_preview",
      "messages_url",
      "sender_display_name",
      "sent_at_human",
      "thread_title",
    ],
    fixture_path: "infra/email/fixtures/messages-unread.json",
  },

  // === Ops (system / integration alerts) ==================================
  {
    opt_out_key: "allocation-drift-flagged",
    template_name: "allocation-drift-flagged",
    title: "Allocation drift",
    description:
      "Sleeves that drifted more than your warn threshold from your target allocation.",
    category: "ops",
    trigger_lambda: "hearth-{env}-allocation-drift-audit (AllocationDriftAuditFn)",
    schedule_human: "Sundays at 9am ET",
    schedule_cron: "cron(0 13 ? * SUN *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "display_name",
      "household_name",
      "portfolio_url",
      "sleeve_count",
      "summary",
      "warn_pp",
    ],
    fixture_path: "infra/email/fixtures/allocation-drift-flagged.json",
  },
  {
    opt_out_key: "ai-cost-cap-warning",
    template_name: "ai-cost-cap-warning",
    title: "AI cost cap warning",
    description:
      "Sent when your monthly AI token usage crosses 80% of your cap.",
    category: "ops",
    trigger_lambda: "hearth-{env}-ai-cost-tracker (AiCostTrackerFn)",
    schedule_human: "Daily at 2am ET (sends only if above threshold)",
    schedule_cron: "cron(0 6 * * ? *)",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: [
      "cost_to_date_human",
      "days_remaining",
      "display_name",
      "month_human",
      "percent_used",
      "settings_url",
      "tokens_cap_human",
      "tokens_used_human",
    ],
    fixture_path: "infra/email/fixtures/ai-cost-cap-warning.json",
  },
  {
    opt_out_key: "beneficiary-audit-flagged",
    template_name: "beneficiary-audit-flagged",
    title: "Beneficiary audit",
    description:
      "Quarterly check on accounts and policies that lack beneficiaries or have stale designations.",
    category: "ops",
    trigger_lambda: "hearth-{env}-quarterly-beneficiary-audit (QuarterlyBeneficiaryAuditFn)",
    schedule_human: "Jan/Apr/Jul/Oct 1st at 8am ET",
    schedule_cron: "cron(0 12 1 1,4,7,10 ? *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "audit_date",
      "display_name",
      "estate_url",
      "flagged_count",
      "household_name",
      "summary",
      "total_examined",
    ],
    fixture_path: "infra/email/fixtures/beneficiary-audit-flagged.json",
  },
  {
    opt_out_key: "document-expiring-soon",
    template_name: "document-expiring-soon",
    title: "Document expiring soon",
    description:
      "Documents (passports, policies, IDs) expiring in the next 90 days.",
    category: "ops",
    trigger_lambda: "hearth-{env}-document-expiration-audit (DocumentExpirationAuditFn)",
    schedule_human: "Saturdays at 9am ET",
    schedule_cron: "cron(0 13 ? * SAT *)",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "display_name",
      "doc_count",
      "documents_url",
      "household_name",
      "summary",
    ],
    fixture_path: "infra/email/fixtures/document-expiring-soon.json",
  },
  {
    opt_out_key: "emergency-doc-ready",
    template_name: "emergency-doc-ready",
    title: "Emergency doc ready",
    description:
      "Sent when your one-page in-case-of-emergency PDF is regenerated.",
    category: "ops",
    trigger_lambda: "hearth-{env}-emergency-doc (EmergencyDocFn)",
    schedule_human: "On-demand",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: [
      "display_name",
      "download_url",
      "generated_at",
      "household_name",
    ],
    fixture_path: "infra/email/fixtures/emergency-doc-ready.json",
  },
  {
    opt_out_key: "plaid-reauth-required",
    template_name: "plaid-reauth-required",
    title: "Plaid reauth required",
    description:
      "Sent when a Plaid connection enters a reauth-required state (e.g., bank password change).",
    category: "ops",
    trigger_lambda: "hearth-{env}-connection-alert (ConnectionAlertFn — DDB stream)",
    schedule_human: "On reauth state change",
    recipients: "user-self",
    opt_outable: false,
    routable: false,
    variables: [
      "display_name",
      "household_name",
      "institution_name",
      "integrations_url",
      "last_error",
    ],
    fixture_path: "infra/email/fixtures/plaid-reauth-required.json",
  },
  {
    opt_out_key: "signal-fired",
    template_name: "signal-fired",
    title: "Signal fired",
    description:
      "A custom price/portfolio alert you configured tripped — direct link to the chart.",
    category: "ops",
    trigger_lambda: "hearth-{env}-signals (SignalsFn)",
    schedule_human: "On signal trip",
    recipients: "user-self",
    opt_outable: true,
    routable: false,
    variables: [
      "chart_url",
      "fired_at_human",
      "rule_name",
      "rule_summary",
      "rule_value",
      "ticker",
    ],
    fixture_path: "infra/email/fixtures/signal-fired.json",
  },
  {
    opt_out_key: "staging-transactions-pending",
    template_name: "staging-transactions-pending",
    title: "Staging transactions pending review",
    description:
      "Plaid/SnapTrade synced transactions waiting for your approval before promoting into the canonical ledger.",
    category: "ops",
    trigger_lambda: "hearth-{env}-staging-promote (StagingPromoteFn — DDB stream)",
    schedule_human: "On batch arrival",
    recipients: "user-self",
    opt_outable: true,
    routable: false,
    variables: [
      "count",
      "display_name",
      "institution_name",
      "staging_url",
    ],
    fixture_path: "infra/email/fixtures/staging-transactions-pending.json",
  },
  {
    opt_out_key: "subscription-audit",
    template_name: "subscription-audit",
    title: "Subscription audit",
    description:
      "Recurring bill amounts that drifted up by more than your warn threshold this month.",
    category: "ops",
    trigger_lambda: "hearth-{env}-subscription-audit (SubscriptionAuditFn)",
    schedule_human: "1st of each month at 8am ET",
    recipients: "household-wide",
    opt_outable: true,
    routable: true,
    variables: [
      "audit_month_human",
      "bills_url",
      "change_count",
      "household_name",
      "threshold_human",
      "changes",
    ],
    fixture_path: "infra/email/fixtures/subscription-audit.json",
  },
];

/**
 * Lookup helper for trigger Lambdas: given an opt_out_key, return true
 * if the user has opted out of THIS purpose. Returns false (= opt-in)
 * if the key is not in the user's email_opt_outs map.
 */
export function isOptedOutByKey(
  email_opt_outs: Readonly<Record<string, boolean>> | undefined,
  opt_out_key: string,
): boolean {
  if (!email_opt_outs) return false;
  return email_opt_outs[opt_out_key] === true;
}

/**
 * Filter helper for the admin UI.
 */
export function emailsByCategory(category: EmailCategory): readonly EmailCatalogEntry[] {
  return EMAIL_CATALOG.filter((e) => e.category === category);
}
